import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { ArrowUpDown, ChevronLeft, ChevronRight, ChevronDown, X, Search, User } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import apiService from '../../../../../services/api.service';
import useDepartmentsData from '../../hooks/useDepartmentsData';
import useEmployeesData from '../../hooks/useEmployeesData';

function DepartmentFilter({ options, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (option) => {
    const isSelected = selected.some((s) => s._id === option._id);
    if (isSelected) {
      onChange(selected.filter((s) => s._id !== option._id));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg',
          'bg-background border border-input',
          'hover:bg-accent',
        )}
      >
        <span className="text-muted-foreground">
          {selected.length > 0 ? `${selected.length} departments` : 'All departments'}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.slice(0, 3).map((item) => (
            <span
              key={item._id}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-primary/10 text-primary"
            >
              {item.name}
              <button type="button" onClick={() => handleSelect(item)}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {selected.length > 3 && (
            <span className="px-2 py-1 text-xs text-muted-foreground">
              +{selected.length - 3} more
            </span>
          )}
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 w-64 mt-1 bg-popover border border-border rounded-lg shadow-lg">
          <div className="p-2 border-b border-border">
            <input
              type="text"
              placeholder="Search departments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded bg-background border border-input"
            />
          </div>
          <div className="max-h-48 overflow-auto p-1">
            {filteredOptions.map((option) => {
              const isSelected = selected.some((s) => s._id === option._id);
              return (
                <button
                  key={option._id}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={cn(
                    'w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent',
                    isSelected && 'bg-primary/10 text-primary',
                  )}
                >
                  {option.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

DepartmentFilter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string }))
    .isRequired,
  selected: PropTypes.arrayOf(PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string }))
    .isRequired,
  onChange: PropTypes.func.isRequired,
};

function EmployeeFilter({ options, selected, onChange, onSearch }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    onSearch(e);
  };

  const handleSelect = (option) => {
    const isSelected = selected.some((s) => s.id === option.id);
    if (isSelected) {
      onChange(selected.filter((s) => s.id !== option.id));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg',
          'bg-background border border-input',
          'hover:bg-accent',
        )}
      >
        <span className="text-muted-foreground">
          {selected.length > 0 ? `${selected.length} employees` : 'All employees'}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.slice(0, 2).map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-primary/10 text-primary"
            >
              {item.email}
              <button type="button" onClick={() => handleSelect(item)}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {selected.length > 2 && (
            <span className="px-2 py-1 text-xs text-muted-foreground">
              +{selected.length - 2} more
            </span>
          )}
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 w-64 mt-1 bg-popover border border-border rounded-lg shadow-lg">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by email (min 3 chars)..."
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-8 pr-2 py-1.5 text-sm rounded bg-background border border-input"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-auto p-1">
            {options.length === 0 ? (
              <p className="px-2 py-3 text-sm text-muted-foreground text-center">
                {search.length < 3 ? 'Type at least 3 characters' : 'No results'}
              </p>
            ) : (
              options.map((option) => {
                const isSelected = selected.some((s) => s.id === option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent',
                      isSelected && 'bg-primary/10 text-primary',
                    )}
                  >
                    {option.email}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

EmployeeFilter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, email: PropTypes.string }))
    .isRequired,
  selected: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, email: PropTypes.string }))
    .isRequired,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
};

function RecentReviews({ startDate, endDate }) {
  const [reviewsData, setReviewsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('updatedAt');

  const { departmentOptions, pickedDepartments, setPickedDepartments } = useDepartmentsData();
  const { employeesOptions, pickedEmployees, fetchEmployees, setPickedEmployees } =
    useEmployeesData();

  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    try {
      const userIdsString = pickedEmployees.map((user) => user.id).join(',');
      const departmentIdsString = pickedDepartments.map((dept) => dept._id).join(',');
      const query = [
        `startDate=${startDate.toISOString()}`,
        `endDate=${endDate.toISOString()}`,
        `skip=${currentPage * rowsPerPage}`,
        `limit=${rowsPerPage}`,
        `sort=${sortField}`,
        `userIds=${userIdsString}`,
        `departmentIds=${departmentIdsString}`,
      ].join('&');

      const response = await apiService.admin.getAdminDashboardReviews(query);
      setReviewsData(response.paginatedResult || []);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, currentPage, rowsPerPage, sortField, pickedEmployees, pickedDepartments]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSort = (field) => {
    setSortField(sortField === field ? `-${field}` : field);
    setCurrentPage(0);
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startIndex = currentPage * rowsPerPage + 1;
  const endIndex = Math.min((currentPage + 1) * rowsPerPage, totalCount);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <div className="w-64">
          <DepartmentFilter
            options={departmentOptions}
            selected={pickedDepartments}
            onChange={setPickedDepartments}
          />
        </div>
        <div className="w-64">
          <EmployeeFilter
            options={employeesOptions}
            selected={pickedEmployees}
            onChange={setPickedEmployees}
            onSearch={fetchEmployees}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Employee</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                Department
              </th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                Review Goal
              </th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                <button
                  type="button"
                  onClick={() => handleSort('updatedAt')}
                  className="flex items-center gap-1 hover:text-foreground"
                >
                  Updated
                  <ArrowUpDown
                    className={cn('h-4 w-4', sortField.includes('updatedAt') && 'text-primary')}
                  />
                </button>
              </th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 5 }, (_, index) => (
                <tr key={index} className="border-t border-border">
                  <td className="p-3">
                    <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </td>
                </tr>
              ))}
            {!isLoading && reviewsData.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-muted-foreground">
                  No reviews found for the selected filters
                </td>
              </tr>
            )}
            {!isLoading &&
              reviewsData.length > 0 &&
              reviewsData.map((review) => (
                <tr key={review._id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                        {review.avatar?.small ? (
                          <img
                            src={review.avatar.small}
                            alt={review.email}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{review.email || 'Unknown'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-sm">{review.departmentName || '-'}</td>
                  <td className="p-3 text-sm">{review.reviewGoal || '-'}</td>
                  <td className="p-3 text-sm text-muted-foreground">
                    {review.updatedAt ? dayjs(review.updatedAt).format('MMM D, YYYY') : '-'}
                  </td>
                  <td className="p-3">
                    <span
                      className={cn(
                        'px-2 py-1 text-xs rounded-full font-medium capitalize',
                        review.status === 'COMPLETED' && 'bg-green-500/10 text-green-600',
                        review.status === 'IN_PROGRESS' && 'bg-yellow-500/10 text-yellow-600',
                        review.status === 'PENDING' && 'bg-gray-500/10 text-gray-600',
                        review.status === 'REVERTED' && 'bg-red-500/10 text-red-600',
                      )}
                    >
                      {review.status?.toLowerCase().replace('_', ' ') || 'Unknown'}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            className="px-2 py-1 rounded border border-input bg-background"
          >
            {[5, 10, 25, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="ml-4">
            {totalCount > 0 ? `${startIndex}-${endIndex} of ${totalCount}` : '0 results'}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="px-2 text-sm">
            Page {currentPage + 1} of {totalPages || 1}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

RecentReviews.propTypes = {
  startDate: PropTypes.instanceOf(dayjs).isRequired,
  endDate: PropTypes.instanceOf(dayjs).isRequired,
};

export default RecentReviews;
